import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSCheckbox } from '../../../shared/components/form-controls';
import { RMSButton } from '../../../shared/components/buttons';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import RMSRecordHistoryButton from '../../../shared/components/modules/RMSRecordHistoryButton';
import SearchAddressAutoComplete from '../../../shared/components/modules/SearchAddressAutoComplete';
import CreateFormResolver from '../../../shared/utility/createFormResolver';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import { notify } from 'reducers/NotifierReducer';
import { useAddressDialog } from 'features/user/hooks/useAddressDialog';
import placeAddressLinkSchema from '../../schema/placeAddressLinkSchema';
import {
  createPlaceAddressLink,
  updatePlaceAddressLink,
  deletePlaceAddressLink,
} from '../../services/addressService';

const PlaceAddressLinkDialog = ({ open, onClose, ptsPlaceID, addressLink = null, onSaved }) => {
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { openAddressDialog } = useAddressDialog();
  const isEditMode = Boolean(addressLink?.ptsAddressID);

  const methods = useForm({
    resolver: CreateFormResolver(placeAddressLinkSchema),
    mode: 'onBlur',
    defaultValues: {
      ptsAddressID: addressLink?.ptsAddressID ?? null,
      IsPrimary: Boolean(addressLink?.IsPrimary),
    },
  });

  const { isDirty } = useFormState({ control: methods.control });

  const ptsAddressID = useWatch({ control: methods.control, name: 'ptsAddressID' });

  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const handleAddressCreated = useCallback(
    (address) => {
      methods.setValue('ptsAddressID', address?.ptsAddressID ?? null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [methods]
  );

  const onSubmit = useCallback(
    async (data) => {
      const payload = {
        ptsPlaceID,
        ptsAddressID: data.ptsAddressID,
        IsPrimary: data.IsPrimary || false,
      };

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () =>
          isEditMode
            ? updatePlaceAddressLink({ ptsPlaceID, ptsAddressID: data.ptsAddressID }, payload)
            : createPlaceAddressLink(payload),
        onSuccess: (savedLink) => {
          dispatch(
            notify(
              isEditMode ? 'Address updated successfully' : 'Address added successfully',
              'success'
            )
          );
          onSaved?.(savedLink);
        },
      });
    },
    [dispatch, isEditMode, onSaved, ptsPlaceID]
  );

  const handleSave = methods.handleSubmit(onSubmit);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on this address. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [isDirty, onClose, requestConfirm]);

  const handleRemove = useCallback(async () => {
    await executeApiAction({
      setLoading: setDeleting,
      dispatch,
      action: () => deletePlaceAddressLink({ ptsPlaceID, ptsAddressID: addressLink.ptsAddressID }),
      onSuccess: () => {
        dispatch(notify('Address removed successfully', 'success'));
        onSaved?.(null);
      },
    });
  }, [addressLink, dispatch, onSaved, ptsPlaceID]);

  return (
    <RMSCommonDialog
      open={open}
      title={isEditMode ? 'Edit Address' : 'Add Address'}
      onClose={requestClose}
      onSave={handleSave}
      loading={submitting || deleting}
      saveDisabled={submitting || deleting}
      maxWidth="sm"
      fullWidth
      closeOnBackdropClick={false}
      saveButton={{
        show: true,
        type: 'simple',
        label: 'Save',
        variant: 'contained',
        color: 'primary',
      }}
      cancelButton={{
        show: true,
        type: 'simple',
        label: 'Close',
        variant: 'outlined',
        color: 'secondary',
      }}
      leftActions={
        isEditMode && (
          <RMSRecordHistoryButton
            disabled={!addressLink?.Created}
            created={addressLink?.Created}
            createdBy={addressLink?.CreatedBy}
            updated={addressLink?.Updated}
            updatedBy={addressLink?.UpdatedBy}
          />
        )
      }
      customButtons={
        isEditMode && (
          <RMSButton
            label="Remove Address From Place Record"
            variant="outlined"
            color="secondary"
            onClick={handleRemove}
            disabled={submitting || deleting}
          />
        )
      }>
      <FormProvider {...methods}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SearchAddressAutoComplete
              name="ptsAddressID"
              label={isEditMode ? 'Address' : 'Search Address'}
              defaultAddressId={ptsAddressID}
              disabled={isEditMode}
              showAddIcon={!isEditMode}
              showDetailsIcon={false}
              onAddIconClick={() =>
                openAddressDialog(null, {
                  closeOnSave: true,
                  onAddressCreated: handleAddressCreated,
                })
              }
            />
          </Grid>
          <Grid item xs={12}>
            <RMSCheckbox name="IsPrimary" label="Is Primary" />
          </Grid>
        </Grid>
      </FormProvider>

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </RMSCommonDialog>
  );
};

export default PlaceAddressLinkDialog;
